import Card from 'components/Card';
import FavoritesButton from 'components/FavoritesButton';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Trek, useTrekker } from 'utils/analytics';

import { ExperienceSource } from '../../model/IExperience';
import ExperienceLinkContainer from '../ExperienceLinkContainer';
import ExperienceCardAvatar from './ExperienceCardAvatar';
import ExperienceCardImage from './ExperienceCardImage';
import ExperienceCardTitle from './ExperienceCardTitle';
import ExperienceItemDetails from './ExperienceItemDetails';

const CSS = require('./ExperienceCard.module.scss');

const INVIEW_OPTIONS = { triggerOnce: true, threshold: 0, rootMargin: '100px' };

interface ExperienceCardProps {
  onClick?: (experience, index) => void;
  experience: ExperienceSource;
  currency?: string;
  nrOfAdults: number;
  nrOfChildren: number;
  analyticsCategory: string;
  index?: number;
  analyticsListName?: string;
  forceHost?: string;
  avatarLabel?: string;
  useAdvertisedPrice?: boolean;
  extraSearchParams?: Record<string, string>;
}

function ExperienceCard({
  onClick,
  experience,
  nrOfAdults,
  nrOfChildren,
  analyticsCategory,
  index = -1,
  analyticsListName,
  forceHost,
  avatarLabel,
  useAdvertisedPrice,
  extraSearchParams,
}: ExperienceCardProps) {
  const trekkerContext = useMemo(
    () => ({
      component: ['experience-card'],
      experienceCard: experience,
      position: index,
      analyticsListName,
    }),
    [experience, index, analyticsListName],
  );

  const { ref, inView } = useInView(INVIEW_OPTIONS);
  const trekker = useTrekker();

  const onEvent = useCallback(
    (type: 'click' | 'view') => {
      trekker.event(type, trekkerContext);
    },
    [trekker, trekkerContext],
  );

  const handleClick = () => {
    if (onClick) {
      onClick(experience, index);
    }
    onEvent('click');
  };

  useEffect(() => {
    if (inView) {
      onEvent('view');
    }
  }, [inView, onEvent]);

  return (
    <Trek context={trekkerContext}>
      <Card data-test-id="ExperienceCard" ref={ref}>
        {inView && (
          <FavoritesButton
            experienceId={experience.id}
            analyticsCategory={analyticsCategory}
          />
        )}
        <ExperienceCardImage
          url={experience.mainPhoto}
          alt={`${experience.title} in ${
            experience.clean_city
          } - ${experience.mainPhoto.split('/').slice(-1)}`}
          visible={inView}
        />
        {experience.experienceType === 'online' && (
          <span className={CSS.OnlineLabel}>online experience</span>
        )}
        <div className={CSS['experienceCard-details']}>
          {inView && (
            <ExperienceCardAvatar
              label={avatarLabel}
              forceHost={forceHost}
              experience={experience}
            />
          )}
          <ExperienceCardTitle
            forceHost={forceHost}
            hosts={experience.hosts}
            city={experience.clean_city}
            account={experience.account}
          />
          <h3 className={CSS['experienceCard-experienceTitle']}>
            <ExperienceLinkContainer
              extraSearchParams={extraSearchParams}
              forceHost={forceHost}
              experience={experience}
              onClick={handleClick}
              className={CSS.TitleLink}
              data-test-id="ExperienceCardLink"
            >
              {experience.title}
            </ExperienceLinkContainer>
          </h3>
          <ExperienceItemDetails
            experience={experience}
            nrOfAdults={nrOfAdults}
            nrOfChildren={nrOfChildren}
            analyticsCategory={analyticsCategory}
            useAdvertisedPrice={useAdvertisedPrice}
          />
        </div>
      </Card>
    </Trek>
  );
}

export default ExperienceCard;
